import {
  HttpClient,
  HttpContext,
  HttpDownloadProgressEvent,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { APP_CONFIG } from 'libs/shared/app-config/src/lib/gb-app-config'
import { concatMap, filter, Observable, of, scan, throwError } from 'rxjs'

/**
 * Interface defining all possible options for an Angular HttpClient request.
 */
export interface RequestOptions {
  headers?: HttpHeaders
  observe?: 'body'
  params?: HttpParams
  reportProgress?: boolean
  responseType?: 'json'
  withCredentials?: boolean
  body?: any
  context?: HttpContext
}

/**
 * GbApiService acts as an base layer for all API interactions,
 * encapsulating common setup like base URL construction, default headers,
 * and handling specialized requests like streaming and file downloads.
 *
 * It utilizes Angular's `inject` function for dependency retrieval, allowing
 * subclasses to be defined without a constructor (or with simplified ones).
 */
@Injectable({
  providedIn: 'root',
})
export class GbApiService {
  /**
   * Default request options applied to all outgoing HTTP calls.
   * Includes credentials (for cookies/sessions) and cache control to ensure fresh data.
   */
  defaultOptions: RequestOptions = {
    withCredentials: true,
    headers: new HttpHeaders({
      'Cache-Control': 'no-cache',
    }),
    params: new HttpParams(),
  }

  /***
   * To ensure Pusher notifications are received even when the current user initiates the request (e.g., in a CMS),
   * an empty `x-realtime-id` header is intentionally sent. This "tricks" the backend into processing the request
   * as if it originated externally, preventing the subsequent Pusher event from being skipped for the requester.
   */
  skipPusherOptions: RequestOptions = {
    headers: new HttpHeaders({ 'x-realtime-id': '' }),
  }

  // Dependencies injected directly using the inject function.
  protected http = inject(HttpClient)
  protected appConfig = inject<any>(APP_CONFIG)

  /**
   * Central method to retrieve the configured API base URL.
   * @returns The configured API URL string.
   */
  protected getApiUrl(): string {
    return this.appConfig.apiUrl
  }

  /**
   * Performs an HTTP GET request.
   * @param endPoint The relative endpoint (e.g., 'users' or 'data/items').
   * @param options Optional request options (headers, params, etc.).
   * @returns An Observable resolving to the response body of type T.
   */
  public get<T>(endPoint: string, options: RequestOptions = {}): Observable<T> {
    const modifiedOptions = {
      ...this.defaultOptions,
      ...options,
    }

    return this.http.get<T>(this.getApiUrl() + endPoint, modifiedOptions)
  }

  /**
   * Performs an HTTP POST request.
   * @param endPoint The relative endpoint.
   * @param params The request body (payload).
   * @param options Optional request options.
   * @returns An Observable resolving to the response body of type T.
   */
  public post<T>(endPoint: string, params: any = {}, options: RequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.post<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  /**
   * Performs an HTTP PUT request to update a resource.
   * @param endPoint The relative endpoint.
   * @param params The request body containing the resource data.
   * @param options Optional request options.
   * @returns An Observable resolving to the response body of type T.
   */
  public put<T>(endPoint: string, params: any, options: RequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.put<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  /**
   * Performs an HTTP DELETE request.
   * @param endPoint The relative endpoint of the resource to delete.
   * @param options Optional request options.
   * @returns An Observable resolving to the response body of type T.
   */
  public delete<T>(endPoint: string, options: RequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.delete<T>(this.getApiUrl() + endPoint, modifiedOptions)
  }

  /**
   * Performs an HTTP PATCH request for partial updates to a resource.
   * @param endPoint The relative endpoint.
   * @param params The request body containing partial update data.
   * @param options Optional request options.
   * @returns An Observable resolving to the response body of type T.
   */
  public patch<T>(endPoint: string, params: any, options: RequestOptions = {}): Observable<T> {
    const modifiedOptions = { ...this.defaultOptions, ...options }
    return this.http.patch<T>(this.getApiUrl() + endPoint, params, modifiedOptions)
  }

  /**
   * Performs a GET request expecting a binary file response (Blob).
   * Useful for downloading files.
   * @param endPoint The relative endpoint.
   * @param options Optional request options.
   * @returns An Observable resolving to a Blob object.
   */
  public download(endPoint: string, options: RequestOptions = {}): Observable<Blob> {
    return this.http.get(this.getApiUrl() + endPoint, {
      ...options,
      responseType: 'blob',
      withCredentials: true,
    }) as Observable<Blob>
  }

  /**
   * Performs a GET request expecting a plain text response.
   * Useful for reading configuration files or non-JSON API responses.
   * @param endPoint The relative endpoint.
   * @param options Optional request options.
   * @returns An Observable resolving to a string.
   */
  public getText(endPoint: string, options: RequestOptions = {}): Observable<string> {
    return this.http.get(this.getApiUrl() + endPoint, {
      ...options,
      responseType: 'text',
      withCredentials: true,
    }) as Observable<string>
  }

  /**
   * Performs a POST request designed to handle server-sent streaming data.
   * It observes 'events' and accumulates the `partialText` chunk by chunk.
   * @param endPoint - The API endpoint that supports streaming.
   * @param body - The body of the request.
   * @param options - Request options (headers, params, etc.).
   * @returns An Observable<string> that emits accumulated text data as it arrives.
   */
  public postStream(endPoint: string, body: any = {}, options: RequestOptions = {}): Observable<string> {
    const modifiedOptions = {
      ...this.defaultOptions,
      ...options,
      // Must observe 'events' to receive HttpDownloadProgressEvent
      observe: 'events' as 'events',
      responseType: 'text' as 'text',
      reportProgress: true,
    }

    return this.http
      .request('POST', this.getApiUrl() + endPoint, {
        ...modifiedOptions,
        body,
        params: { streaming: true }, // Add streaming flag if needed by the server
      })
      .pipe(
        // Use scan to accumulate and check for streaming errors
        scan(
          (acc, event) => {
            // Check for initial non-200 response header
            if (event.type === HttpEventType.ResponseHeader && event.status !== 200) {
              return { ...acc, hasError: true }
            }
            // Process streaming data chunks
            if (event.type === HttpEventType.DownloadProgress) {
              const partial = (event as HttpDownloadProgressEvent).partialText
              if (partial) {
                // If an error was flagged previously, treat new text as the error message
                if (acc.hasError) {
                  return { ...acc, error: { error: partial } }
                }
                // Accumulate the successful stream text
                return { ...acc, text: partial }
              }
            }
            // Capture the response body for error responses (small bodies may
            // not trigger a DownloadProgress event)
            if (event.type === HttpEventType.Response && acc.hasError && !acc.error) {
              const body = (event as HttpResponse<string>).body
              return { ...acc, error: { error: body || 'Non-200 status received' } }
            }
            return acc
          },
          // Initial state for the accumulator
          { hasError: false, error: null as { error: string } | null, text: '' },
        ),
        // Switch from the state object to either an error or the accumulated text
        concatMap((state) => {
          if (state.error && state.error.error) {
            // Throw an error if one was detected in the stream
            return throwError(() => state.error)
          }
          return of(state.text)
        }),
        // Only emit non-empty accumulated strings
        filter((text) => !!text),
      )
  }
}
